<template>
  <modal
    size="full"
    :title="title"
    :open="open"
    @close="closeModal"
  >
    <div v-if="loading" class="loading-container">
      <spinner inline />
    </div>

    <div v-else-if="display === 'search'" class="container">
      <collection-search-add
        ref="collectionSearchAdd"
        @add="handleAdd"
        @upload="handleUpload"
        @certupload="handleCertUpload"
      />
    </div>

    <div v-else-if="display === 'edit'">
      <div v-if="card" class="grid">
        <section>
          <card-collection-fields
            v-model="card"
            :is-public-collection="collection.public"
          />
        </section>

        <section>
          <card-details-fields v-model="card" :disabled="isLadderCard" />

          <card-advanced-fields v-model="card" is-collection />
        </section>
      </div>
    </div>

    <div v-else class="container">
      <collection-card-estimate
        v-model="card"
        :collection-id="collectionId"
        :display="display"
        @changeDisplay="changeDisplay"
        @close="closeModal"
      />
    </div>

    <template #footer>
      <div v-if="display !== 'edit'">
        <portal-target name="collection-edit-card-modal-footer" />
      </div>

      <div v-if="display === 'edit'">
        <btn
          :title="disabled ? 'Fill out required fields first' : ''"
          :disabled="disabled"
          :loading="saving"
          @click="submit"
        >
          {{ buttonText }}
        </btn>
      </div>
    </template>
  </modal>
</template>

<script>
import firebase from 'firebase/compat/app';
import { mapState } from 'vuex';
import clone from 'lodash.clonedeep';
import * as Sentry from '@sentry/vue';

import { DEFAULT_CARD_DETAILS, DEFAULT_CARD_COLLECTION } from '@cardladder/shared/constants';
import { getCollectionCardToSave } from '@cardladder/shared/utils/collection.util';

import {
  cardService,
  authService,
  indexService,
  playerService,
} from '@/services';

import CardDetailsFields from '@/cards/CardDetailsFields';
import CardAdvancedFields from '@/cards/CardAdvancedFields';
import CollectionSearchAdd from './CollectionSearchAdd';
import CollectionCardEstimate from './CollectionCardEstimate';
import CardCollectionFields from './CardCollectionFields';

export default {
  components: {
    CollectionSearchAdd,
    CardDetailsFields,
    CardAdvancedFields,
    CollectionCardEstimate,
    CardCollectionFields,
  },

  data() {
    return {
      open: false,
      id: null,
      collectionId: null,
      card: null,
      saving: false,
      loading: false,
      display: 'search',
    };
  },

  mounted() {
    this.$bus.$on('collection-edit-card', this.handleEdit);
    this.$bus.$on('collection-add-card', this.handleAdd);
    this.$bus.$on('collection-edit-card-value', this.handleEditValue);
    this.$bus.$on('keyboard-shortcut-s', this.handleKeyboardSave);
  },

  beforeDestroy() {
    this.$bus.$off('collection-edit-card', this.handleEdit);
    this.$bus.$off('collection-add-card', this.handleAdd);
    this.$bus.$off('collection-edit-card-value', this.handleEditValue);
    this.$bus.$off('keyboard-shortcut-s', this.handleKeyboardSave);
  },

  watch: {
    'card.sold'(newValue, oldValue) {
      if (oldValue === false && newValue === true) {
        this.card.dateSold = new Date();
      }
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collection: (state) => state.collection,
    }),

    isLadderCard() {
      return Boolean(this.card?.cardId);
    },

    editing() {
      return Boolean(this.id);
    },

    title() {
      if (this.display === 'edit') {
        return this.editing ? 'Edit Collection Card' : 'Add Card to Collection';
      } if (this.display === 'search') {
        return 'Add Card to Collection';
      }

      return 'Estimate Value';
    },

    buttonText() {
      return this.editing ? 'Save' : 'Add';
    },

    disabled() {
      if (!this.card || typeof this.card.investment !== 'number' || !this.card.datePurchased || !this.card.quantity || this.card.quantity < 1) {
        return true;
      }

      if (this.card.sold && !this.card.willManageQuantity && (!this.card.dateSold || !this.card.priceSold)) {
        return true;
      }

      if (this.card.cardId) {
        return false;
      }

      if (!this.card.collectionId) {
        return true;
      }

      return !this.card.player
        || (!this.card.condition && !this.card.grader)
        || !this.card.year
        || !this.card.set
        || !this.card.category;
    },
  },

  methods: {
    handleEdit(request) {
      this.reset();

      if (request && request.id && request.collectionId) {
        this.id = request.id;
        this.collectionId = request.collectionId;
        this.display = request.display;
        this.load();
        this.display = 'edit';
      } else {
        this.display = 'search';
        this.collectionId = this.collection.id;
      }

      setTimeout(() => {
        this.open = true;
      }, 200);
    },

    handleEditValue({ id, display = 'valueIntro', collectionId }) {
      this.reset();

      if (id && collectionId) {
        this.id = id;
        this.collectionId = collectionId;
      }

      this.load();

      setTimeout(() => {
        this.open = true;
        this.display = display;
      }, 200);
    },

    async handleAdd(card) {
      this.loading = true;
      this.reset();

      this.card = {
        ...clone(DEFAULT_CARD_DETAILS),
        condition: 'Raw',
        ...clone(DEFAULT_CARD_COLLECTION),
        ...clone(card),
        uid: this.user.uid,
        collectionId: this.collection.id,
      };

      this.collectionId = this.collection.id;
      this.display = 'edit';
      this.open = true;

      await this.setCategoryFromPlayer();

      if (card.cert && card.grader && !card.set) {
        try {
          const { data: collectionCardFromCert } = await firebase.functions().httpsCallable('httpbuildcollectioncard')({
            cert: card.cert,
            grader: card.grader,
          });

          if (collectionCardFromCert.datePurchased) {
            collectionCardFromCert.datePurchased = new Date(collectionCardFromCert.datePurchased);
          }

          this.card = {
            ...this.card,
            ...collectionCardFromCert,
          };
        } catch (e) {
          // swallow
        }
      }

      if (!this.card.cardId && this.card.profileId && this.card.grade && this.card.grader) {
        this.card.useGradedCardAutomatic = true;
      }

      if (this.card.imageUrl) {
        this.card.image = this.card.imageUrl;
        delete this.card.imageUrl;
      }

      if (this.card.imageBackUrl) {
        this.card.imageBack = this.card.imageBackUrl;
        delete this.card.imageBackUrl;
      }

      this.loading = false;
    },

    handleUpload() {
      this.closeModal();
      this.$bus.$emit('upload-collection');
    },

    handleCertUpload() {
      this.closeModal();
      this.$bus.$emit('upload-collection', true);
    },

    changeDisplay(display) {
      this.display = display;
    },

    handleKeyboardSave() {
      if (this.open) {
        this.submit();
      }
    },

    async load() {
      this.loading = true;

      if (this.editing) {
        try {
          this.card = await authService.getCollectionCard(this.user.uid, this.collectionId, this.id);
        } catch (e) {
          this.$toasted.error('Error loading the card');
          Sentry.captureException(e);
        }
      } else {
        this.card = {
          ...clone(DEFAULT_CARD_DETAILS),
          ...clone(DEFAULT_CARD_COLLECTION),
          collectionId: this.collection.id,
        };
      }

      this.loading = false;
    },

    async submit() {
      this.saving = true;
      const collectionId = this.collectionId || this.collection.id;
      const movingCollections = this.editing && collectionId !== this.card.collectionId;

      try {
        const collectionCard = await getCollectionCardToSave(
          this.card,
          cardService,
          indexService,
          firebase,
          this.card.collectionId,
          this.user.uid,
        );

        const createdCard = await authService.createOrUpdateCollectionCard(
          this.user.uid,
          this.editing ? collectionId : this.card.collectionId,
          collectionCard,
        );

        if (movingCollections) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          await authService.moveCollectionCard(
            this.user.uid,
            collectionId,
            this.card.collectionId,
            this.card.id,
          );
        }

        if (movingCollections) {
          this.$bus.$emit('collection-changed', { remove: this.card.collectionCardId });
          this.$bus.$emit('card-profile-changed');
          this.$toasted.success('Card saved!');
          this.closeModal();
        } else if (this.editing) {
          this.$bus.$emit('card-profile-changed');
          this.$bus.$emit('collection-changed', { edit: { id: collectionCard.collectionCardId, ...createdCard } });
          this.$toasted.success('Card saved!');
          this.closeModal();
        } else {
          this.$router.replace(`/collection?cardId=${createdCard.id}&profile=collection`);
          this.card.id = createdCard.id;
          this.$bus.$emit('collection-changed', { add: true });

          let alreadyHasValueSetup = false;

          if (createdCard.useGradedCardAutomatic) {
            alreadyHasValueSetup = true;
          }

          if (createdCard.playerIndexId || createdCard.otherIndexId) {
            alreadyHasValueSetup = true;
          }

          if (alreadyHasValueSetup) {
            this.closeModal();
          } else {
            this.display = 'valueIntro';
          }
        }
      } catch (e) {
        this.$toasted.error('Error saving the card');
        Sentry.captureException(e);
      }

      this.saving = false;
    },

    async setCategoryFromPlayer() {
      if (this.card && this.card.player && !this.card.category) {
        const player = await playerService.getPlayer(this.card.player);

        if (player && player.category) {
          this.card.category = player.category;
        }
      }
    },

    closeModal() {
      if (this.$refs?.form?.reset) {
        this.$refs.form.reset();
      }

      if (this.$refs?.collectionSearchAdd?.reset) {
        this.$refs.collectionSearchAdd.reset();
      }

      this.open = false;

      setTimeout(() => {
        this.reset();
      }, 100);
    },

    reset() {
      this.id = null;
      this.collectionId = null;
      this.display = 'search';
      this.card = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: $gp;

  @media($medium-down) {
    display: block;
  }
}

.card-details-fields {
  margin-bottom: $gp;
}

.loading-container {
  padding: $gp;
  display: flex;
  justify-content: center;
}
</style>
